import { FiAlertCircle, FiArrowUp, FiCheck } from "react-icons/fi";
import styled from "styled-components";

import { CircularLoader } from "../../common/CircularLoader";
import FlexBox from "../../common/FlexBox";
import { Body2, H4 } from "../../common/Headings";
import {
  ACCENT_300,
  DAVYS_GREY_500,
  DAVYS_GREY_700,
  DAVYS_GREY_800,
  ERROR_RED,
  MOSS_GREEN_800,
  SUCCESS_GREEN,
  SUCCESS_GREEN_800,
  lightOrange,
  white,
} from "../../common/colors";

const PromptContainer = styled(FlexBox)`
  flex-direction: column;
  background: ${ACCENT_300};
  border-radius: 0.5rem;
`;

const ErrorBanner = styled(FlexBox)`
  flex-direction: column;
  background: ${ERROR_RED[100]};
  border: 1px solid ${ERROR_RED[500]};
  border-radius: 0.5rem;
  padding: 1rem;
  gap: 0.5rem;
`;

const ErrorTitle = styled(FlexBox)`
  align-items: center;
  gap: 0.5rem;
  color: ${ERROR_RED[500]};
`;

const SuccessBanner = styled(FlexBox)`
  flex-direction: column;
  background: ${SUCCESS_GREEN[100]};
  border: 1px solid ${SUCCESS_GREEN[500]};
  border-radius: 0.5rem;
  padding: 1rem;
  gap: 0.5rem;
`;

const SuccessTitle = styled(FlexBox)`
  align-items: center;
  gap: 0.5rem;
  color: ${MOSS_GREEN_800};
`;

const BannerTitleText = styled(Body2)`
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
`;

const ReviewingRow = styled(FlexBox)`
  padding: 1rem;
  align-items: center;
  gap: 0.5rem;
  background: ${ACCENT_300};
  border-radius: 0.5rem;
  border: 1px solid ${DAVYS_GREY_500};
`;

const PromptBar = styled(FlexBox)`
  flex: 1;
  border: 1px solid ${DAVYS_GREY_500};
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  align-items: flex-end;
  gap: 0.5rem;
  background: ${white};
  min-height: 192px;
  max-height: 192px;
`;

const PromptInput = styled.textarea`
  flex: 1;
  border: none;
  outline: none;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.5;
  color: ${DAVYS_GREY_800};
  resize: none;
  &::placeholder {
    color: ${DAVYS_GREY_500};
  }
  height: 100%;
`;

const SendButton = styled.button`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: none;
  background: ${lightOrange};
  color: ${white};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:disabled {
    background: ${DAVYS_GREY_500};
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditingCustomizationPanel = ({
  isReviewing,
  hasError,
  isSuccess,
  promptInput,
  validationResult,
  errorMessage,
  setPromptInput,
  isPromptSendEnabled,
  handleSendPrompt,
}) => (
  <>
    <FlexBox column rowGap="0.25rem">
      <H4 bold>Add more context to the original prompt</H4>
      <Body2 color={DAVYS_GREY_700}>
        Your requirement to the AI summary will be additionally added on top of
        the Original summary structure.
      </Body2>
    </FlexBox>

    {isReviewing && (
      <ReviewingRow>
        <CircularLoader fitContent />
        <Body2>Reviewing your customisation...</Body2>
      </ReviewingRow>
    )}

    {hasError && (
      <ErrorBanner>
        <ErrorTitle>
          <FiAlertCircle size={16} color={ERROR_RED[800]} />
          <BannerTitleText bold color={ERROR_RED[800]}>
            Cannot apply this customisation
          </BannerTitleText>
        </ErrorTitle>
        <Body2>{errorMessage}</Body2>
      </ErrorBanner>
    )}

    {isSuccess && (
      <SuccessBanner>
        <SuccessTitle>
          <FiCheck size={16} color={SUCCESS_GREEN_800} />
          <BannerTitleText bold color={MOSS_GREEN_800}>
            Customisation Ready
          </BannerTitleText>
        </SuccessTitle>
        <Body2>{validationResult}</Body2>
      </SuccessBanner>
    )}

    <PromptContainer width="100%" padding={"12px"}>
      <PromptBar>
        <PromptInput
          placeholder={`Type here or add prompt to the chat box for AI to generate a customised summary structure for you....

Example: Provide 2–3 suggested next-step interventions or focus areas for the next session based on the client's current presentation and today's discussion.`}
          value={promptInput}
          onChange={e => setPromptInput(e.target.value)}
        />
        <SendButton
          type="button"
          disabled={!isPromptSendEnabled}
          onClick={handleSendPrompt}
        >
          <FiArrowUp size={16} />
        </SendButton>
      </PromptBar>
    </PromptContainer>
  </>
);

export default EditingCustomizationPanel;
